import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import PanelLayout from '../../components/PanelLayout';
import {
  StatCard, Card, CardBody, CardHeader, Button, Badge, StatusBadge,
  Table, Modal, FormGroup, Label, Input, Select, Textarea, EmptyState,
  PageLoader, Tabs, ConfirmDialog, SectionHeader, IconButton
} from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import api, { fmt, imgUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const NAV = [
  { to:'/admin', icon:'📊', label:'Dashboard' },
  { divider: true, label:'Catalog' },
  { to:'/admin/products', icon:'📦', label:'Products' },
  { to:'/admin/categories', icon:'🏷️', label:'Categories' },
  { divider: true, label:'Sales' },
  { to:'/admin/orders', icon:'🛒', label:'Orders' },
  { to:'/admin/payments', icon:'💳', label:'Payments' },
  { to:'/admin/coupons', icon:'🎫', label:'Coupons' },
  { divider: true, label:'Users' },
  { to:'/admin/customers', icon:'👥', label:'Customers' },
  { to:'/admin/vendors', icon:'🏪', label:'Vendors' },
  { divider: true, label:'Content' },
  { to:'/admin/banners', icon:'🖼️', label:'Banners' },
  { to:'/admin/shipping', icon:'🚚', label:'Shipping' },
  { to:'/admin/settings', icon:'⚙️', label:'Settings' },
  { to:'/admin/reports', icon:'📈', label:'Reports' },
];

function RequireAdmin({ children }) {
  const { role } = useAuth();
  if (role !== 'admin') return <Navigate to="/login" replace />;
  return children;
}

/* ── Dashboard ──────────────────────────────────────────────── */
function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      api.get('/reports/revenue?period=30'),
    ]).then(([dash, rev]) => {
      setData({ ...dash, revenue: rev });
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div>
      <SectionHeader title="Dashboard" subtitle="Welcome back, Admin" />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:28 }}>
        <StatCard icon="💰" label="Total Revenue" value={fmt.currency(data?.stats?.orders?.revenue || 0)} color="green" />
        <StatCard icon="🛒" label="Total Orders" value={data?.stats?.orders?.total || 0} color="blue" />
        <StatCard icon="👥" label="Customers" value={data?.stats?.customers?.total || 0} color="orange" />
        <StatCard icon="📦" label="Products" value={data?.stats?.products?.total || 0} color="purple" />
        <StatCard icon="⏳" label="Pending Orders" value={data?.stats?.orders?.pending || 0} color="red" />
        <StatCard icon="✅" label="Delivered" value={data?.stats?.orders?.delivered || 0} color="green" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>
        <Card>
          <CardHeader><h4>Revenue (30 days)</h4></CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data?.revenue?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fontSize:11 }} />
                <Tooltip formatter={v => fmt.currency(v)} />
                <Line type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
        <Card>
          <CardHeader><h4>Orders (30 days)</h4></CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data?.revenue?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fontSize:11 }} />
                <Tooltip />
                <Bar dataKey="orders" fill="var(--primary-light)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <Card>
          <CardHeader><h4>Recent Orders</h4></CardHeader>
          <Table
            columns={[
              { key:'order_number', label:'Order', render:r=><span style={{fontWeight:600}}>#{r.order_number}</span> },
              { key:'customer_name', label:'Customer' },
              { key:'total', label:'Total', render:r=>fmt.currency(r.total) },
              { key:'order_status', label:'Status', render:r=><StatusBadge status={r.order_status} /> },
            ]}
            data={data?.recentOrders || []}
          />
        </Card>
        <Card>
          <CardHeader><h4>Top Products</h4></CardHeader>
          <Table
            columns={[
              { key:'name', label:'Product' },
              { key:'sold', label:'Sold' },
              { key:'revenue', label:'Revenue', render:r=>fmt.currency(r.revenue) },
            ]}
            data={data?.topProducts || []}
          />
        </Card>
      </div>
    </div>
  );
}

/* ── Products ───────────────────────────────────────────────── */
function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name:'', description:'', short_description:'', price:'', sale_price:'', sku:'', category_id:'', stock:'', featured:false, status:'active', tax_rate:'0' });
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [editingImages, setEditingImages] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get('/products?limit=100'), api.get('/categories')]);
      setProducts(p.products || []);
      setCategories(c.categories || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name:'', description:'', short_description:'', price:'', sale_price:'', sku:'', category_id:'', stock:'0', featured:false, status:'active', tax_rate:'0' });
    setVariants([{ color:'', color_hex:'#000000', size:'', stock:'10', price_modifier:'0' }]);
    setImages([]);
    setEditingImages([]);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name:p.name, description:p.description||'', short_description:p.short_description||'', price:p.price, sale_price:p.sale_price||'', sku:p.sku||'', category_id:p.category_id||'', stock:p.stock, featured:!!p.featured, status:p.status, tax_rate:p.tax_rate||'0' });
    setVariants(p.variants?.length ? p.variants : [{ color:'', color_hex:'#000000', size:'', stock:'10', price_modifier:'0' }]);
    setEditingImages(p.images || []);
    setImages([]);
    setEditingImages([]);
    setModalOpen(true);
  };

  const deleteExistingImage = async (imageId) => {
    try {
      await api.delete(`/products/images/${imageId}`);
      setEditingImages(prev => prev.filter(i => i.id !== imageId));
      toast.success('Image removed');
    } catch { toast.error('Failed to remove image'); }
  };

  const save = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    fd.append('variants', JSON.stringify(variants));
    images.forEach(f => fd.append('images', f));
    try {
      if (editing) await api.uploadPut(`/products/${editing.id}`, fd);
      else await api.upload('/products', fd);
      toast.success(editing ? 'Product updated' : 'Product created');
      setModalOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteProduct = async () => {
    await api.delete(`/products/${deleteId}`);
    toast.success('Product deleted');
    load();
  };

  const addVariant = () => setVariants(v => [...v, { color:'', color_hex:'#000000', size:'', stock:'10', price_modifier:'0' }]);
  const updateVariant = (i, field, val) => setVariants(v => v.map((x,idx) => idx===i ? {...x,[field]:val} : x));
  const removeVariant = (i) => setVariants(v => v.filter((_,idx) => idx!==i));

  return (
    <div>
      <SectionHeader title="Products" subtitle={`${products.length} total`}
        action={<Button onClick={openCreate}>+ Add Product</Button>} />

      <Table loading={loading}
        columns={[
          { key:'image', label:'', render:r=><img src={imgUrl(r.primary_image)} alt="" style={{width:44,height:44,borderRadius:'var(--radius-sm)',objectFit:'cover',background:'var(--gray-100)'}} /> },
          { key:'name', label:'Product', render:r=><div><div style={{fontWeight:600}}>{r.name}</div><div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>SKU: {r.sku||'—'}</div></div> },
          { key:'category_name', label:'Category', render:r=>r.category_name||'—' },
          { key:'price', label:'Price', render:r=><div><div style={{fontWeight:600,color:'var(--primary)'}}>{fmt.currency(r.sale_price||r.price)}</div>{r.sale_price&&<div style={{fontSize:'0.78rem',color:'var(--gray-400)',textDecoration:'line-through'}}>{fmt.currency(r.price)}</div>}</div> },
          { key:'stock', label:'Stock', render:r=><span style={{color:r.stock<5?'var(--error)':'var(--success)',fontWeight:600}}>{r.stock}</span> },
          { key:'status', label:'Status', render:r=><StatusBadge status={r.status} /> },
          { key:'actions', label:'', render:r=>(
            <div style={{display:'flex',gap:6}}>
              <IconButton $size="sm" onClick={()=>openEdit(r)} title="Edit">✏️</IconButton>
              <IconButton $size="sm" onClick={()=>setDeleteId(r.id)} title="Delete" style={{color:'var(--error)'}}>🗑</IconButton>
            </div>
          )},
        ]}
        data={products}
      />

      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title={editing?'Edit Product':'New Product'} width="720px"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Save Product</Button></>}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <FormGroup style={{gridColumn:'1/-1'}}><Label>Product Name *</Label><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Price *</Label><Input type="number" step="0.01" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Sale Price</Label><Input type="number" step="0.01" value={form.sale_price} onChange={e=>setForm(f=>({...f,sale_price:e.target.value}))} placeholder="Optional" /></FormGroup>
          <FormGroup><Label>SKU</Label><Input value={form.sku} onChange={e=>setForm(f=>({...f,sku:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Stock</Label><Input type="number" value={form.stock} onChange={e=>setForm(f=>({...f,stock:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Category</Label>
            <Select value={form.category_id} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))}>
              <option value="">Select category</option>
              {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Status</Label>
            <Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </Select>
          </FormGroup>
          <FormGroup><Label>Tax Rate (%)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={e=>setForm(f=>({...f,tax_rate:e.target.value}))} /></FormGroup>
          <FormGroup style={{display:'flex',alignItems:'center',gap:8,marginTop:8}}>
            <input type="checkbox" id="featured" checked={form.featured} onChange={e=>setForm(f=>({...f,featured:e.target.checked}))} style={{width:16,height:16,accentColor:'var(--primary)'}} />
            <label htmlFor="featured" style={{fontSize:'0.88rem',fontWeight:500}}>Featured Product</label>
          </FormGroup>
          <FormGroup style={{gridColumn:'1/-1'}}><Label>Short Description</Label><Input value={form.short_description} onChange={e=>setForm(f=>({...f,short_description:e.target.value}))} /></FormGroup>
          <FormGroup style={{gridColumn:'1/-1'}}><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{minHeight:100}} /></FormGroup>
          <FormGroup style={{gridColumn:'1/-1'}}>
            <Label>Product Images {editing ? '(add more images below — existing images kept)' : '(select up to 10 images)'}</Label>
            <input
              type="file"
              multiple
              accept="image/*"
              style={{width:'100%',padding:'10px 14px',border:'1.5px solid var(--gray-200)',borderRadius:'var(--radius-sm)',fontSize:'0.9rem',background:'#fff',fontFamily:'var(--font-body)',cursor:'pointer'}}
              onChange={e => {
                const newFiles = Array.from(e.target.files);
                setImages(prev => {
                  const combined = [...prev, ...newFiles];
                  return combined.slice(0, 10); // max 10
                });
                e.target.value = ''; // reset so same file can be added again
              }}
            />
            <p style={{fontSize:'0.78rem',color:'var(--gray-400)',marginTop:4}}>
              You can click multiple times to add more images (max 10 total)
            </p>
            {images.length > 0 && (
              <div style={{marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>
                {images.map((f,i)=>(
                  <div key={i} style={{position:'relative',width:64,height:64,borderRadius:'var(--radius-sm)',overflow:'hidden',border:'1px solid var(--gray-200)'}}>
                    <img src={URL.createObjectURL(f)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    <button onClick={()=>setImages(imgs=>imgs.filter((_,idx)=>idx!==i))} style={{position:'absolute',top:2,right:2,width:16,height:16,borderRadius:'50%',background:'rgba(0,0,0,.6)',color:'#fff',border:'none',cursor:'pointer',fontSize:'10px',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {editing && editingImages.length > 0 && (
              <div style={{marginTop:8}}>
                <div style={{fontSize:'0.78rem',color:'var(--gray-500)',marginBottom:6}}>Existing images (click ✕ to delete):</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {editingImages.map((img)=>(
                    <div key={img.id} style={{position:'relative',width:64,height:64,borderRadius:'var(--radius-sm)',overflow:'hidden',border: img.is_primary?'2px solid var(--primary)':'1px solid var(--gray-200)'}}>
                      <img src={imgUrl(img.image_url)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                      {img.is_primary && <div style={{position:'absolute',bottom:0,left:0,right:0,background:'rgba(45,106,79,.8)',color:'#fff',fontSize:'9px',textAlign:'center',padding:'1px 0'}}>Primary</div>}
                      <button onClick={()=>deleteExistingImage(img.id)} style={{position:'absolute',top:2,right:2,width:16,height:16,borderRadius:'50%',background:'rgba(220,38,38,.8)',color:'#fff',border:'none',cursor:'pointer',fontSize:'10px',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </FormGroup>
        </div>

        {/* Variants */}
        <div style={{marginTop:16,borderTop:'1px solid var(--gray-100)',paddingTop:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <Label style={{margin:0}}>Color & Size Variants</Label>
            <Button $variant="outline" $size="sm" onClick={addVariant}>+ Add Variant</Button>
          </div>
          {variants.map((v,i)=>(
            <div key={i} style={{display:'grid',gridTemplateColumns:'1fr auto 1fr 80px 80px auto',gap:8,marginBottom:10,alignItems:'end'}}>
              <div><Label>Color</Label><Input value={v.color} onChange={e=>updateVariant(i,'color',e.target.value)} placeholder="e.g. Red" /></div>
              <div><Label>Hex</Label><input type="color" value={v.color_hex||'#000000'} onChange={e=>updateVariant(i,'color_hex',e.target.value)} style={{width:42,height:42,border:'1.5px solid var(--gray-200)',borderRadius:6,cursor:'pointer'}} /></div>
              <div><Label>Size</Label><Input value={v.size} onChange={e=>updateVariant(i,'size',e.target.value)} placeholder="e.g. M, XL" /></div>
              <div><Label>Stock</Label><Input type="number" value={v.stock} onChange={e=>updateVariant(i,'stock',e.target.value)} /></div>
              <div><Label>+Price</Label><Input type="number" step="0.01" value={v.price_modifier} onChange={e=>updateVariant(i,'price_modifier',e.target.value)} /></div>
              <IconButton $size="sm" onClick={()=>removeVariant(i)} style={{color:'var(--error)',marginTop:22}}>✕</IconButton>
            </div>
          ))}
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={deleteProduct}
        title="Delete Product" message="This action cannot be undone. Delete this product?" danger />
    </div>
  );
}

/* ── Categories ─────────────────────────────────────────────── */
function Categories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name:'', description:'' });
  const [deleteId, setDeleteId] = useState(null);

  const load = () => api.get('/categories').then(d=>setCats(d.categories||[])).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v])=>fd.append(k,v));
    try { await api.upload('/categories',fd); toast.success('Category created'); setModalOpen(false); load(); }
    catch(e){ toast.error(e.message); }
  };

  return (
    <div>
      <SectionHeader title="Categories" action={<Button onClick={()=>{ setForm({name:'',description:''}); setModalOpen(true); }}>+ Add Category</Button>} />
      <Table loading={loading}
        columns={[
          { key:'name', label:'Name', render:r=><span style={{fontWeight:600}}>{r.name}</span> },
          { key:'slug', label:'Slug', render:r=><code style={{fontSize:'0.8rem',background:'var(--gray-100)',padding:'2px 6px',borderRadius:4}}>{r.slug}</code> },
          { key:'is_active', label:'Status', render:r=><StatusBadge status={r.is_active?'active':'blocked'} /> },
          { key:'actions', label:'', render:r=><IconButton $size="sm" onClick={()=>setDeleteId(r.id)} style={{color:'var(--error)'}}>🗑</IconButton> },
        ]}
        data={cats}
      />
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="New Category"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></>}>
        <FormGroup><Label>Name</Label><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Image</Label><Input type="file" accept="image/*" onChange={e=>setForm(f=>({...f,_image:e.target.files[0]}))} /></FormGroup>
      </Modal>
      <ConfirmDialog open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={async()=>{ await api.delete(`/categories/${deleteId}`); load(); }} title="Delete Category" message="Delete this category?" danger />
    </div>
  );
}

/* ── Orders (Admin) ─────────────────────────────────────────── */
function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [selected, setSelected] = useState(null);
  const [statusModal, setStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ order_status:'', courier_name:'', tracking_number:'', notes:'' });

  const load = useCallback(() => {
    setLoading(true);
    const q = tab !== 'all' ? `?status=${tab}` : '';
    api.get(`/orders${q}`).then(d=>setOrders(d.orders||[])).finally(()=>setLoading(false));
  }, [tab]);

  useEffect(()=>{ load(); },[load]);

  const openStatusModal = (order) => {
    setSelected(order);
    setStatusForm({ order_status: order.order_status, courier_name:'', tracking_number:'', notes:'' });
    setStatusModal(true);
  };

  const updateStatus = async () => {
    try {
      await api.patch(`/orders/${selected.id}/status`, statusForm);
      toast.success('Status updated');
      setStatusModal(false);
      load();
    } catch(e){ toast.error(e.message); }
  };

  const TABS = [['all','All'],['pending','Pending'],['confirmed','Confirmed'],['packed','Packed'],['shipped','Shipped'],['delivered','Delivered'],['cancelled','Cancelled']];

  return (
    <div>
      <SectionHeader title="Orders" subtitle={`${orders.length} orders`} />
      <Tabs tabs={TABS.map(([v,l])=>({value:v,label:l}))} active={tab} onChange={t=>{ setTab(t); }} />
      <Table loading={loading}
        columns={[
          { key:'order_number', label:'Order', render:r=><span style={{fontWeight:700}}>#{r.order_number}</span> },
          { key:'customer_name', label:'Customer', render:r=><div><div style={{fontWeight:500}}>{r.customer_name}</div><div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{r.customer_email}</div></div> },
          { key:'created_at', label:'Date', render:r=>fmt.date(r.created_at) },
          { key:'item_count', label:'Items', render:r=>`${r.item_count} items` },
          { key:'total', label:'Total', render:r=><span style={{fontWeight:700,color:'var(--primary)'}}>{fmt.currency(r.total)}</span> },
          { key:'payment_status', label:'Payment', render:r=><StatusBadge status={r.payment_status} /> },
          { key:'order_status', label:'Status', render:r=><StatusBadge status={r.order_status} /> },
          { key:'actions', label:'', render:r=><Button $variant="outline" $size="sm" onClick={()=>openStatusModal(r)}>Update</Button> },
        ]}
        data={orders}
      />
      <Modal open={statusModal} onClose={()=>setStatusModal(false)} title={`Update Order #${selected?.order_number}`}
        footer={<><Button $variant="secondary" onClick={()=>setStatusModal(false)}>Cancel</Button><Button onClick={updateStatus}>Update Status</Button></>}>
        <FormGroup><Label>Order Status</Label>
          <Select value={statusForm.order_status} onChange={e=>setStatusForm(f=>({...f,order_status:e.target.value}))}>
            {['pending','confirmed','packed','shipped','out_for_delivery','delivered','cancelled','returned'].map(s=><option key={s} value={s}>{fmt.orderStatus(s)}</option>)}
          </Select>
        </FormGroup>
        <FormGroup><Label>Courier Name</Label><Input value={statusForm.courier_name} onChange={e=>setStatusForm(f=>({...f,courier_name:e.target.value}))} placeholder="e.g. FedEx, DHL" /></FormGroup>
        <FormGroup><Label>Tracking Number</Label><Input value={statusForm.tracking_number} onChange={e=>setStatusForm(f=>({...f,tracking_number:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Notes</Label><Textarea value={statusForm.notes} onChange={e=>setStatusForm(f=>({...f,notes:e.target.value}))} style={{minHeight:80}} /></FormGroup>
      </Modal>
    </div>
  );
}

/* ── Customers ──────────────────────────────────────────────── */
function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/customers${search?`?search=${search}`:''}`).then(d=>setCustomers(d.customers||[])).finally(()=>setLoading(false));
  }, [search]);

  useEffect(()=>{ const t=setTimeout(load,400); return ()=>clearTimeout(t); },[load]);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus==='active'?'blocked':'active';
    await api.patch(`/admin/customers/${id}`,{status:newStatus});
    toast.success(`Customer ${newStatus}`);
    load();
  };

  return (
    <div>
      <SectionHeader title="Customers" subtitle={`${customers.length} total`} />
      <div style={{marginBottom:16}}><Input placeholder="Search by name or email..." value={search} onChange={e=>setSearch(e.target.value)} style={{maxWidth:320}} /></div>
      <Table loading={loading}
        columns={[
          { key:'name', label:'Name', render:r=><span style={{fontWeight:600}}>{r.name}</span> },
          { key:'email', label:'Email' },
          { key:'phone', label:'Phone', render:r=>r.phone||'—' },
          { key:'status', label:'Status', render:r=><StatusBadge status={r.status} /> },
          { key:'created_at', label:'Joined', render:r=>fmt.date(r.created_at) },
          { key:'actions', label:'', render:r=>(
            <div style={{display:'flex',gap:6}}>
              <Button $variant={r.status==='active'?'danger':'outline'} $size="sm" onClick={()=>toggleStatus(r.id,r.status)}>
                {r.status==='active'?'Block':'Unblock'}
              </Button>
            </div>
          )},
        ]}
        data={customers}
      />
    </div>
  );
}

/* ── Vendors ────────────────────────────────────────────────── */
function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({name:'',email:'',password:'',phone:''});

  const load = () => api.get('/admin/vendors').then(d=>setVendors(d.vendors||[])).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try { await api.post('/admin/vendors',form); toast.success('Vendor created'); setModalOpen(false); load(); }
    catch(e){ toast.error(e.message); }
  };

  const toggle = async (id, status) => {
    await api.patch(`/admin/vendors/${id}`,{status:status==='active'?'blocked':'active'});
    toast.success('Vendor updated'); load();
  };

  return (
    <div>
      <SectionHeader title="Vendors" action={<Button onClick={()=>{ setForm({name:'',email:'',password:'',phone:''}); setModalOpen(true); }}>+ Add Vendor</Button>} />
      <Table loading={loading}
        columns={[
          { key:'name', label:'Name', render:r=><span style={{fontWeight:600}}>{r.name}</span> },
          { key:'email', label:'Email' },
          { key:'phone', label:'Phone', render:r=>r.phone||'—' },
          { key:'status', label:'Status', render:r=><StatusBadge status={r.status} /> },
          { key:'created_at', label:'Added', render:r=>fmt.date(r.created_at) },
          { key:'actions', label:'', render:r=>(
            <Button $variant={r.status==='active'?'danger':'outline'} $size="sm" onClick={()=>toggle(r.id,r.status)}>
              {r.status==='active'?'Block':'Activate'}
            </Button>
          )},
        ]}
        data={vendors}
      />
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="Add Vendor"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Create Vendor</Button></>}>
        <FormGroup><Label>Full Name</Label><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Password</Label><Input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} /></FormGroup>
      </Modal>
    </div>
  );
}

/* ── Payments ───────────────────────────────────────────────── */
function Payments() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/payments/logs').then(d=>setLogs(d.logs||[])).finally(()=>setLoading(false)); },[]);
  return (
    <div>
      <SectionHeader title="Payment Logs" />
      <Table loading={loading}
        columns={[
          { key:'order_number', label:'Order', render:r=><span style={{fontWeight:600}}>#{r.order_number}</span> },
          { key:'customer_name', label:'Customer' },
          { key:'paypal_order_id', label:'PayPal ID', render:r=><code style={{fontSize:'0.78rem'}}>{r.paypal_order_id?.slice(0,16)}...</code> },
          { key:'amount', label:'Amount', render:r=><span style={{fontWeight:700,color:'var(--primary)'}}>{fmt.currency(r.amount)}</span> },
          { key:'status', label:'Status', render:r=><StatusBadge status={r.status==='completed'?'paid':r.status} /> },
          { key:'created_at', label:'Date', render:r=>fmt.datetime(r.created_at) },
        ]}
        data={logs}
      />
    </div>
  );
}

/* ── Coupons ────────────────────────────────────────────────── */
function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({code:'',type:'percentage',value:'',min_order_amount:'0',max_uses:'',expires_at:''});

  const load = () => api.get('/coupons').then(d=>setCoupons(d.coupons||[])).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try { await api.post('/coupons',form); toast.success('Coupon created'); setModalOpen(false); load(); }
    catch(e){ toast.error(e.message); }
  };

  const toggle = async (id, active) => {
    await api.put(`/coupons/${id}`,{is_active: active?0:1});
    load();
  };

  return (
    <div>
      <SectionHeader title="Coupons" action={<Button onClick={()=>{ setForm({code:'',type:'percentage',value:'',min_order_amount:'0',max_uses:'',expires_at:''}); setModalOpen(true); }}>+ Create Coupon</Button>} />
      <Table loading={loading}
        columns={[
          { key:'code', label:'Code', render:r=><code style={{fontWeight:700,background:'var(--gray-100)',padding:'3px 8px',borderRadius:4}}>{r.code}</code> },
          { key:'type', label:'Type', render:r=><Badge $color="info">{r.type==='percentage'?`${r.value}% OFF`:`$${r.value} OFF`}</Badge> },
          { key:'min_order_amount', label:'Min Order', render:r=>fmt.currency(r.min_order_amount) },
          { key:'used_count', label:'Used', render:r=>`${r.used_count}${r.max_uses?`/${r.max_uses}`:''}` },
          { key:'expires_at', label:'Expires', render:r=>r.expires_at?fmt.date(r.expires_at):'Never' },
          { key:'is_active', label:'Status', render:r=><StatusBadge status={r.is_active?'active':'blocked'} /> },
          { key:'actions', label:'', render:r=><Button $variant={r.is_active?'danger':'outline'} $size="sm" onClick={()=>toggle(r.id,r.is_active)}>{r.is_active?'Disable':'Enable'}</Button> },
        ]}
        data={coupons}
      />
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="Create Coupon"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Create</Button></>}>
        <FormGroup><Label>Code</Label><Input value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="e.g. SAVE20" /></FormGroup>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <FormGroup><Label>Type</Label>
            <Select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed Amount ($)</option>
            </Select>
          </FormGroup>
          <FormGroup><Label>Value</Label><Input type="number" value={form.value} onChange={e=>setForm(f=>({...f,value:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Min Order ($)</Label><Input type="number" value={form.min_order_amount} onChange={e=>setForm(f=>({...f,min_order_amount:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Max Uses</Label><Input type="number" value={form.max_uses} onChange={e=>setForm(f=>({...f,max_uses:e.target.value}))} placeholder="Unlimited" /></FormGroup>
          <FormGroup style={{gridColumn:'1/-1'}}><Label>Expires At</Label><Input type="datetime-local" value={form.expires_at} onChange={e=>setForm(f=>({...f,expires_at:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

/* ── Banners ────────────────────────────────────────────────── */
function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({title:'',subtitle:'',link:'',sort_order:'0'});
  const [imgFile, setImgFile] = useState(null);

  const load = () => api.get('/banners').then(d=>setBanners(d.banners||[])).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v])=>fd.append(k,v));
    if(imgFile) fd.append('image',imgFile);
    try { await api.upload('/banners',fd); toast.success('Banner created'); setModalOpen(false); load(); }
    catch(e){ toast.error(e.message); }
  };

  return (
    <div>
      <SectionHeader title="Banners" action={<Button onClick={()=>{ setForm({title:'',subtitle:'',link:'',sort_order:'0'}); setImgFile(null); setModalOpen(true); }}>+ Add Banner</Button>} />
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
        {loading ? <PageLoader /> : banners.map(b=>(
          <Card key={b.id}>
            <img src={imgUrl(b.image)} alt={b.title} style={{width:'100%',height:140,objectFit:'cover'}} />
            <CardBody $sm>
              <div style={{fontWeight:600}}>{b.title}</div>
              <div style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{b.subtitle}</div>
              <div style={{display:'flex',gap:8,marginTop:10}}>
                <StatusBadge status={b.is_active?'active':'blocked'} />
                <Button $variant="danger" $size="sm" onClick={async()=>{ await api.delete(`/banners/${b.id}`); load(); }}>Delete</Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="Add Banner"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></>}>
        <FormGroup><Label>Title</Label><Input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Subtitle</Label><Input value={form.subtitle} onChange={e=>setForm(f=>({...f,subtitle:e.target.value}))} /></FormGroup>
        <FormGroup><Label>Link URL</Label><Input value={form.link} onChange={e=>setForm(f=>({...f,link:e.target.value}))} placeholder="/shop" /></FormGroup>
        <FormGroup><Label>Image *</Label><Input type="file" accept="image/*" onChange={e=>setImgFile(e.target.files[0])} /></FormGroup>
        <FormGroup><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:e.target.value}))} /></FormGroup>
      </Modal>
    </div>
  );
}

/* ── Shipping ───────────────────────────────────────────────── */
function Shipping() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({name:'',regions:'',base_rate:'',free_above:'',estimated_days:''});

  const load = () => api.get('/shipping').then(d=>setZones(d.zones||[])).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try { await api.post('/shipping',form); toast.success('Zone created'); setModalOpen(false); load(); }
    catch(e){ toast.error(e.message); }
  };

  return (
    <div>
      <SectionHeader title="Shipping Zones" action={<Button onClick={()=>{ setForm({name:'',regions:'',base_rate:'',free_above:'',estimated_days:''}); setModalOpen(true); }}>+ Add Zone</Button>} />
      <Table loading={loading}
        columns={[
          { key:'name', label:'Zone', render:r=><span style={{fontWeight:600}}>{r.name}</span> },
          { key:'regions', label:'Regions', render:r=>r.regions||'—' },
          { key:'base_rate', label:'Base Rate', render:r=>fmt.currency(r.base_rate) },
          { key:'free_above', label:'Free Above', render:r=>r.free_above?fmt.currency(r.free_above):'—' },
          { key:'estimated_days', label:'Est. Days', render:r=>r.estimated_days||'—' },
          { key:'is_active', label:'Status', render:r=><StatusBadge status={r.is_active?'active':'blocked'} /> },
        ]}
        data={zones}
      />
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="Add Shipping Zone"
        footer={<><Button $variant="secondary" onClick={()=>setModalOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></>}>
        <FormGroup><Label>Zone Name</Label><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Domestic" /></FormGroup>
        <FormGroup><Label>Regions</Label><Input value={form.regions} onChange={e=>setForm(f=>({...f,regions:e.target.value}))} placeholder="e.g. India, USA" /></FormGroup>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <FormGroup><Label>Base Rate ($)</Label><Input type="number" step="0.01" value={form.base_rate} onChange={e=>setForm(f=>({...f,base_rate:e.target.value}))} /></FormGroup>
          <FormGroup><Label>Free Shipping Above ($)</Label><Input type="number" step="0.01" value={form.free_above} onChange={e=>setForm(f=>({...f,free_above:e.target.value}))} /></FormGroup>
        </div>
        <FormGroup><Label>Estimated Delivery Days</Label><Input value={form.estimated_days} onChange={e=>setForm(f=>({...f,estimated_days:e.target.value}))} placeholder="e.g. 3-5 days" /></FormGroup>
      </Modal>
    </div>
  );
}

/* ── Settings ───────────────────────────────────────────────── */
function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ api.get('/settings').then(d=>setSettings(d.settings||{})).finally(()=>setLoading(false)); },[]);

  const save = async () => {
    setSaving(true);
    try { await api.put('/settings',settings); toast.success('Settings saved'); }
    catch(e){ toast.error(e.message); }
    finally{ setSaving(false); }
  };

  if(loading) return <PageLoader />;

  const fields = [
    { key:'site_name', label:'Site Name' },
    { key:'site_email', label:'Support Email' },
    { key:'site_phone', label:'Phone' },
    { key:'currency', label:'Currency Code' },
    { key:'currency_symbol', label:'Currency Symbol' },
    { key:'tax_rate', label:'Tax Rate (%)' },
    { key:'shipping_default', label:'Default Shipping Cost ($)' },
    { key:'free_shipping_above', label:'Free Shipping Above ($)' },
  ];

  return (
    <div>
      <SectionHeader title="Site Settings" />
      <Card style={{maxWidth:600}}>
        <CardBody>
          {fields.map(f=>(
            <FormGroup key={f.key}>
              <Label>{f.label}</Label>
              <Input value={settings[f.key]||''} onChange={e=>setSettings(s=>({...s,[f.key]:e.target.value}))} />
            </FormGroup>
          ))}
          <Button onClick={save} disabled={saving}>{saving?'Saving...':'Save Settings'}</Button>
        </CardBody>
      </Card>
    </div>
  );
}

/* ── Reports ────────────────────────────────────────────────── */
function Reports() {
  const [period, setPeriod] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    setLoading(true);
    Promise.all([
      api.get(`/reports/revenue?period=${period}`),
      api.get('/reports/top-products'),
      api.get('/reports/top-customers'),
    ]).then(([rev,prods,custs])=>{
      setData({ revenue:rev, products:prods, customers:custs });
    }).finally(()=>setLoading(false));
  },[period]);

  return (
    <div>
      <SectionHeader title="Reports & Analytics"
        action={<Select value={period} onChange={e=>setPeriod(e.target.value)} style={{width:'auto'}}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </Select>}
      />
      {loading ? <PageLoader /> : (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:24}}>
            <StatCard icon="💰" label="Total Revenue" value={fmt.currency(data?.revenue?.totals?.total_revenue||0)} color="green" />
            <StatCard icon="🛒" label="Total Orders" value={data?.revenue?.totals?.total_orders||0} color="blue" />
            <StatCard icon="📊" label="Avg Order Value" value={fmt.currency(data?.revenue?.totals?.avg_order||0)} color="orange" />
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:24}}>
            <Card>
              <CardHeader><h4>Revenue Trend</h4></CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data?.revenue?.daily||[]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{fontSize:11}} tickFormatter={d=>d?.slice(5)} />
                    <YAxis tick={{fontSize:11}} />
                    <Tooltip formatter={v=>fmt.currency(v)} />
                    <Line type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
            <Card>
              <CardHeader><h4>Top Products</h4></CardHeader>
              <Table columns={[
                { key:'name', label:'Product', render:r=><span style={{fontWeight:500,fontSize:'0.88rem'}}>{r.name}</span> },
                { key:'units_sold', label:'Sold' },
                { key:'revenue', label:'Revenue', render:r=>fmt.currency(r.revenue) },
              ]} data={data?.products?.products||[]} />
            </Card>
          </div>
          <Card>
            <CardHeader><h4>Top Customers</h4></CardHeader>
            <Table columns={[
              { key:'name', label:'Customer', render:r=><span style={{fontWeight:500}}>{r.name}</span> },
              { key:'email', label:'Email' },
              { key:'order_count', label:'Orders' },
              { key:'total_spent', label:'Total Spent', render:r=><span style={{fontWeight:700,color:'var(--primary)'}}>{fmt.currency(r.total_spent)}</span> },
            ]} data={data?.customers?.customers||[]} />
          </Card>
        </>
      )}
    </div>
  );
}

/* ── Admin Router ───────────────────────────────────────────── */
export default function AdminPanel() {
  return (
    <RequireAdmin>
      <PanelLayout navItems={NAV} role="Admin">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="categories" element={<Categories />} />
          <Route path="orders" element={<Orders />} />
          <Route path="payments" element={<Payments />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="customers" element={<Customers />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="banners" element={<Banners />} />
          <Route path="shipping" element={<Shipping />} />
          <Route path="settings" element={<Settings />} />
          <Route path="reports" element={<Reports />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </PanelLayout>
    </RequireAdmin>
  );
}
